import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import moment from 'moment';
import {
  variantSources,
  discrepancyReview
} from 'src/util/variantPathogenicity';
import { VARIANT_INTERPRETATION_OPTIONS } from '../clinical-notes-section/forms/formService';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { classificationText } from './classificationText';

/**
 * Review a laboratory/ClinVar classification disagreement.
 *
 * The dialog carries the whole task: it restates both source classifications,
 * takes the clinician's determination, takes an optional reason, and shows every
 * previous review. Making the determination HERE rather than requiring it
 * beforehand matches the actual clinical act — you decide in light of the
 * disagreement, not before seeing it — and removes a dead end where the button
 * was disabled until the reader went and used a different control.
 *
 * Two deliberate properties:
 *
 *  - The determination is applied ONLY on confirm. Cancel genuinely cancels;
 *    on a classification field, a control that silently persisted a half-made
 *    choice would be dangerous.
 *  - Previous reviews are shown and never edited. Correction is a NEW review,
 *    so a reclassification by a second clinician reads as exactly that rather
 *    than as the first opinion disappearing.
 *
 * Built on the shared CustomDialog rather than raw MUI, so the title bar,
 * spacing, button styling and type scale come from the same place as every
 * other clinical modal. Hand-rolling those is what made this dialog read
 * smaller and sit differently than the rest of the app.
 */
const DiscrepancyReviewDialog = ({
  open,
  variant,
  reviewerName,
  dateFormat = 'DD/MM/YYYY',
  onCancel,
  onConfirm
}) => {
  const t = useAutoTranslation();

  const sources = variantSources(variant);
  const lab = sources.find((s) => s.key === 'lab');
  const clinvar = sources.find((s) => s.key === 'clinvar');
  const review = discrepancyReview(variant);

  const [determination, setDetermination] = useState('');
  const [note, setNote] = useState('');

  // Seed from the recorded classification each time the dialog opens, so a
  // re-review starts from the current position rather than blank — but keep it
  // local until confirm.
  useEffect(() => {
    if (!open) return;
    setDetermination(variant?.clinicianInterpretation || '');
    setNote('');
  }, [open, variant?.clinicianInterpretation]);

  const hgvs =
    variant?.variantC || variant?.variantG || variant?.variantP || '';

  const isRereview = review.history.length > 0;

  return (
    <CustomDialog
      open={!!open}
      handleClose={onCancel}
      allowBackgroundClose={onCancel}
      showActions={true}
      // Synchronous confirm, so the wrapper must not await a promise from it.
      isParentHandlingLoading={true}
      // A review without a determination would record that the conflict was
      // handled while carrying no clinical position on it.
      saveDisabled={!determination}
      saveBtnLabel={'I have reviewed both sources'}
      onSave={() => onConfirm({ determination, note: note.trim() })}
      title={
        isRereview
          ? 'Review classification discrepancy again'
          : 'Review classification discrepancy'
      }
      minHeight={200}
    >
      <Box>
        <Typography sx={{ mb: 1.5 }}>
          {t(
            "The reporting laboratory and ClinVar do not agree on this variant. Record your determination below. Confirming states that you have reviewed both sources and that your determination stands as this record's position."
          )}
        </Typography>

        {hgvs && (
          <Typography sx={{ fontFamily: 'monospace', mb: 1.5 }}>
            {hgvs}
          </Typography>
        )}

        {/* Restate both positions. The clinician is confirming they have seen
            THESE values, so they cannot be left implicit. */}
        <Box sx={{ mb: 2 }}>
          <Typography>
            <strong>{t('Laboratory:')}</strong>{' '}
            {classificationText(lab?.classification, t) ||
              t('no classification reported')}
          </Typography>
          <Typography>
            <strong>ClinVar:</strong>{' '}
            {classificationText(clinvar?.classification, t) ||
              t('no classification reported')}
          </Typography>
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t('Your determination')}
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Select
            displayEmpty
            value={determination}
            onChange={(e) => setDetermination(e.target.value)}
            renderValue={(v) =>
              v ? classificationText(v, t) : t('Select a classification')
            }
            inputProps={{ 'aria-label': t('Your determination') }}
          >
            {VARIANT_INTERPRETATION_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {t(o.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label={t('Reason (optional)')}
          placeholder={t(
            'e.g. the laboratory classification predates the ClinVar submission'
          )}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.8 }}>
          {t(
            'Both source classifications remain visible on the record. The pedigree will show that this discrepancy has been clinician-reviewed rather than that it is unresolved. If either source, or your determination, changes later, the disagreement is a new one and will be flagged again.'
          )}
        </Typography>

        {reviewerName && (
          <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.8 }}>
            {t('Recorded against: {{reviewerName}}', { reviewerName })}
          </Typography>
        )}

        {isRereview && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
              {t('Previous reviews')}
            </Typography>
            {/* Never edited or removed: the audit question is who stood behind
                what, and when. */}
            {review.history.map((h, i) => (
              <Box
                key={`${h.reviewedAt || i}`}
                sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}
              >
                <CheckCircleOutlinedIcon
                  sx={{ fontSize: 18, color: 'green', mt: '2px' }}
                />
                <Box>
                  <Typography variant="body2">
                    {classificationText(h.determination, t) ||
                      t('No determination recorded')}
                    {h.reviewedBy ? ` — ${h.reviewedBy}` : ''}
                    {h.reviewedAt
                      ? `, ${moment(h.reviewedAt).format(dateFormat)}`
                      : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {t(
                      'Reviewed against laboratory {{lab}} and ClinVar {{clinvar}}',
                      {
                        lab:
                          classificationText(h.labAtReview, t) ||
                          t('no classification'),
                        clinvar:
                          classificationText(h.clinvarAtReview, t) ||
                          t('no classification')
                      }
                    )}
                  </Typography>
                  {h.note && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      {h.note}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>
    </CustomDialog>
  );
};

export default DiscrepancyReviewDialog;
